import React, { useState, useEffect } from 'react';
import { build } from '@/lib/buildInfo';
import { promptMeta, promptMirror } from '@/lib/promptMeta';
import { getExperimentDebugInfo, EXP_BIN_CTA_V1 } from '@/lib/experiments';
import { getZohoIntegrationStatus } from '@/lib/zohoConfig';
import { getAllFlags } from '@/lib/flags';
import { supabase } from '@/integrations/supabase/client';
import { shouldShowConsentBanner } from '@/lib/privacy/region';

export default function Health() {
  const [flags, setFlags] = useState<Record<string, string>>({});
  const [consultingCount, setConsultingCount] = useState<number>(0);
  const [mailerHealth, setMailerHealth] = useState<any>(null);

  useEffect(() => {
    getAllFlags()
      .then(setFlags)
      .catch(() => setFlags({}));

    // Get consulting orders count
    const fetchCount = async () => {
      try {
        const { count } = await supabase
          .from('consulting_orders')
          .select('id', { count: 'exact', head: true });
        setConsultingCount(count || 0);
      } catch (error) {
        setConsultingCount(0);
      }
    };
    fetchCount();

    // Check mailer health
    const checkMailer = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('mailer-health');
        if (!error && data) {
          setMailerHealth(data);
        }
      } catch (error) {
        console.error('Failed to check mailer health:', error);
      }
    };
    checkMailer();
  }, []);

  const checksumOk = promptMeta.id === promptMirror.id && promptMeta.title === promptMirror.title;

  // Get experiment status
  const binCtaExperiment = getExperimentDebugInfo(EXP_BIN_CTA_V1);

  // Get Zoho integration status
  const zohoStatus = getZohoIntegrationStatus();

  // P20250925-123: Get Zoho Unified status
  const [zohoUnifiedStatus, setZohoUnifiedStatus] = useState<any>(null);

  useEffect(() => {
    import('@/lib/integrations/zohoUnified').then((module) => {
      module.getZohoUnifiedStatus().then(setZohoUnifiedStatus);
    });
  }, []);

  const healthData = {
    ok: true,
    checksum: checksumOk ? 'ok' : 'fail',
    supabase: import.meta.env.VITE_SUPABASE_URL ? 'configured' : 'missing',
    functionsBase: import.meta.env.VITE_FUNCTIONS_BASE || 'missing',
    env: import.meta.env.MODE || 'unknown',
    ts: new Date().toISOString(),
    build: {
      sha: build.sha,
      builtAt: build.builtAt,
    },
    prompt: {
      id: promptMeta.id,
      title: promptMeta.title,
      at: promptMeta.timestamp,
    },
    flags: {
      binCta: flags.binCta || 'CONTROL_BUY_NOW',
      priceFraming: flags.priceFraming || 'MO_PLUS_DAY',
      offerFloor: flags.offerFloor || '80',
      devDiscount: flags.devDiscount || 'OFF',
    },
    exports: {
      routes: ['inventory', 'offers', 'chats', 'chat-messages', 'invoices'],
      lastPack: null,
    },
    seo: {
      robotsMode: 'DISALLOW_ADMIN',
      robotsCustom: false,
      sitemapDetected: false,
      lastAuditAt: null,
    },
    accounts: {
      magicLink: true,
      usersTable: true,
      userAssetsTable: true,
      adminUsers: true,
    },
    billing: {
      graceFail: 2,
      hardStopFail: 4,
      cancellationsOpen: 0,
    },
    offers: {
      leaseOnly: true,
      rtoRoutesRedirect: true,
      promptId: 'P20250925-073',
    },
    testing: {
      adminMagicLink: true,
    },
    inventory: {
      import: {
        enabled: true,
        lastImportAt: null,
      },
      export: true,
      rule: 'lease=max(roundNice(BIN*0.03),49)',
    },
    support: {
      faqs: 13,
      topics: 5,
      botKbSync: false,
      rtoRemoved: true,
      promptId: 'P20250929-FA02',
    },
    experiments: {
      registry: Object.keys({}).length, // Will be populated from admin_settings
      apiConnected: false, // Set to true when POSTHOG_API_KEY is configured
      defaults: {
        split: 0.5,
        minSample: 400,
        minDays: 3,
        srmP: 0.001,
      },
      [EXP_BIN_CTA_V1]: {
        variant: binCtaExperiment.variant,
        source: binCtaExperiment.source,
        posthog_ready: binCtaExperiment.posthogReady,
      },
    },
    analytics: {
      featureEnabled: import.meta.env.VITE_FEATURE_ANALYTICS_ENABLED !== 'false',
      posthog: import.meta.env.VITE_POSTHOG_KEY ? 'configured' : 'missing',
      clarity: import.meta.env.VITE_CLARITY_ID ? 'configured' : 'missing',
      analyticsEnabled: import.meta.env.VITE_ANALYTICS_ENABLED !== 'false',
      clarityEnabled: import.meta.env.VITE_CLARITY_ENABLED === 'true' && import.meta.env.PROD,
      salesiqEnabled: import.meta.env.VITE_SALESIQ_ENABLED === 'true' && import.meta.env.PROD,
    },
    email: {
      transactional: {
        provider: mailerHealth?.provider || 'resend',
        subdomain: 'send.attaindomains.com',
        from: mailerHealth?.from || 'notify@send.attaindomains.com',
        replyTo: mailerHealth?.replyTo || 'Contact@AttainDomains.com',
        resendConfigured: mailerHealth?.resendConfigured ?? false,
        resendUsable: mailerHealth?.resendUsable ?? false,
        transactionalSendDryRun: !import.meta.env.PROD,
      },
      contact: {
        provider: 'zoho-smtp',
        from: 'Contact@AttainDomains.com',
        to: 'Contact@AttainDomains.com',
      },
    },
    integrations: {
      zohoUnified: zohoUnifiedStatus || {
        oauthConnected: false,
        salesiq: false,
        bookings: false,
        books: false,
        bigin: false,
        desk: false,
        sign: false,
      },
      clarity: {
        enabled: import.meta.env.VITE_CLARITY_ENABLED === 'true',
        idConfigured: !!import.meta.env.VITE_CLARITY_ID,
        requireConsent: import.meta.env.VITE_REQUIRE_CONSENT === 'true',
      },
      posthog: true,
      promptId: 'P20250925-123',
    },
    landers: {
      csvInventory: true,
      csvPersuasion: true,
      hostRouting: true,
    },
    consent: {
      mode: 'spa-heuristic',
      euLikeActive: true,
      usBanner: false,
      method: 'timezone+language',
      detectedEU: shouldShowConsentBanner(),
      geoHeader: 'none (SPA - no SSR)',
    },
    decisions: {
      version: 'CL-v2',
      updatedAt: '2025-10-01T00:00:00Z',
      latestPID: 'P20250925-123',
      sha256: 'placeholder', // Will be computed from decision log
    },
    dataPipeline: {
      webhooks: ['zoho-unified'],
      storage: 'owner-packs',
      cron: true,
      opsDigests: true,
    },
    admin: {
      basePath: '/manage', // P20250925-153: Changed from /admin
      apiOnline: true,
      protected: true,
      routes: [
        '/manage/owner',
        '/manage/data',
        '/manage/inventory',
        '/manage/experiments',
        '/manage/prompts',
        '/manage/exports',
        '/manage/seo',
        '/manage/users',
        '/manage/settings',
      ],
      pages: 9,
      quickLinks: [
        import.meta.env.VITE_SUPABASE_URL,
        import.meta.env.VITE_POSTHOG_KEY,
        import.meta.env.VITE_CLARITY_ID,
      ].filter(Boolean).length,
      clarityId: import.meta.env.VITE_CLARITY_ID || null,
      shortcuts: true,
    },
    prompts: {
      lastFromMeta: promptMeta.id,
      nextSuggestion: 'P20250925-063',
      syncOK: checksumOk,
    },
    metabase: {
      configurable: true,
      settingsPath: '/admin/settings',
    },
    consulting: {
      packages: 2,
      ordersCount: consultingCount,
      webhookActive: true,
      promptId: 'P20250925-079',
    },
    quality: {
      a11yBase: true,
      budgets: {
        LCP: '2.5s',
        CLS: '0.1',
        JS: '300KB',
      },
      urls: 6,
      promptId: 'P20250925-080',
    },
    links: {
      docs: '/docs/',
      transcripts: '/docs/transcripts/',
    },
    promptId: 'P20250925-079',
  };

  // Check if JSON format is requested
  const urlParams = new URLSearchParams(window.location.search);
  const isJsonFormat = urlParams.get('format') === 'json';

  if (isJsonFormat) {
    // Return JSON response
    document.body.innerHTML = `<pre>${JSON.stringify(healthData, null, 2)}</pre>`;
    document.head.innerHTML = '<meta http-equiv="Content-Type" content="application/json">';
    return null;
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-6 text-foreground">Health Check</h1>
        <pre className="bg-muted p-6 rounded-lg font-mono text-sm text-foreground overflow-auto border">
          {JSON.stringify(healthData, null, 2)}
        </pre>
      </div>
    </div>
  );
}
